import type { LucideIcon } from "lucide-react";
import {
  Archive,
  BarChart,
  Calendar,
  CalendarCheck,
  CalendarDays,
  CheckSquare,
  ClipboardList,
  Drama,
  FileBarChart,
  FileText,
  GanttChart,
  Landmark,
  LayoutGrid,
  Percent,
  PieChart,
  Receipt,
  Settings,
  Sheet,
  Sparkles,
  StickyNote,
  Tag,
  UserRound,
  Users,
} from "lucide-react";

/**
 * The single source of project navigation: labels, icons, routes and groups.
 * The sidebar, the breadcrumbs and the command palette all read from here,
 * so a label cannot differ between them.
 */

export type ProjectStepId = "script" | "characters" | "schedule" | "budget";

/** AI pipelines tracked in the project's `feature_statuses` rows. */
export type ProjectStepFeature =
  | "script_breakdown"
  | "coverage"
  | "character_breakdown"
  | "schedule"
  | "budget"
  | "tax_incentives";

export interface ProjectStepItemDef {
  /** Stable id used in analytics and tests. */
  id: string;
  label: string;
  /** Route segment under the step, e.g. `breakdown` in `/script/breakdown`. */
  segment: string;
  /** Icon shown in the command palette. The sidebar lists items as text only. */
  icon: LucideIcon;
  /** Other route segments under the step that belong to this item. */
  alsoMatches?: readonly string[];
}

export interface ProjectStepDef {
  id: ProjectStepId;
  label: string;
  icon: LucideIcon;
  /** Route segment of the step, e.g. `script`. */
  segment: string;
  /** Writer licenses do not include this step. */
  studioOnly: boolean;
  /** Pipeline whose run decides Ready or Not started. */
  primaryFeature: ProjectStepFeature;
  /** Pipelines that also surface Running or Failed on this step. */
  secondaryFeatures: readonly ProjectStepFeature[];
  /** Inline explanation shown when a Writer project opens a Studio step. */
  studioLockDescription?: string;
  items: readonly ProjectStepItemDef[];
}

export type ProjectLinkId = "project-settings" | "team-members" | "ai-credits";

export interface ProjectLinkDef {
  id: ProjectLinkId;
  /** Full label, used by the command palette and the breadcrumbs. */
  label: string;
  /** Shorter label in the sidebar, where the "Project" section names the context. */
  sidebarLabel: string;
  /** Listed in the sidebar. The palette and breadcrumbs list every link. */
  inSidebar: boolean;
  icon: LucideIcon;
  /** Route the link opens, relative to the project. */
  segment: string;
  /** Project-relative routes that highlight this link (segment-boundary prefix match). */
  matchSegments: readonly string[];
}

export const PROJECT_NAV_SECTION_LABELS = {
  steps: "Production",
  links: "Project",
} as const;

export const STUDIO_LICENSE_LABEL = "Studio";

export const PROJECT_STEPS: readonly ProjectStepDef[] = [
  {
    id: "script",
    label: "Script",
    icon: FileText,
    segment: "script",
    studioOnly: false,
    primaryFeature: "script_breakdown",
    secondaryFeatures: ["coverage"],
    items: [
      {
        id: "script-breakdown",
        label: "Breakdown",
        segment: "breakdown",
        icon: BarChart,
        alsoMatches: ["outline", "snapshots", "compare"],
      },
      { id: "script-coverage", label: "Coverage", segment: "coverage", icon: CheckSquare },
      { id: "script-notes", label: "Notes", segment: "notes", icon: StickyNote },
      { id: "script-tags", label: "Tags", segment: "tags", icon: Tag },
      { id: "script-report", label: "Report", segment: "report", icon: FileBarChart },
    ],
  },
  {
    id: "characters",
    label: "Characters & Cast",
    icon: Users,
    segment: "characters",
    studioOnly: false,
    primaryFeature: "character_breakdown",
    secondaryFeatures: [],
    items: [
      { id: "characters-breakdown", label: "Characters", segment: "breakdown", icon: BarChart },
      { id: "characters-summary", label: "Summary", segment: "summary", icon: ClipboardList },
      { id: "characters-actors", label: "Cast & Actors", segment: "actors", icon: Drama },
    ],
  },
  {
    id: "schedule",
    label: "Schedule",
    icon: Calendar,
    segment: "schedule",
    studioOnly: true,
    primaryFeature: "schedule",
    secondaryFeatures: [],
    studioLockDescription:
      "Stripboards, Day Out of Days and the shooting calendar come with a Studio license. Your script and characters stay as they are.",
    items: [
      { id: "schedule-stripboard", label: "Stripboard", segment: "stripboard", icon: LayoutGrid },
      { id: "schedule-roadmap", label: "Roadmap", segment: "roadmap", icon: GanttChart },
      { id: "schedule-boneyard", label: "Boneyard", segment: "boneyard", icon: Archive },
      {
        id: "schedule-day-out-of-days",
        label: "Day Out of Days",
        segment: "dayoutofdays",
        icon: CalendarDays,
      },
      {
        id: "schedule-shooting-calendar",
        label: "Shooting Calendar",
        segment: "shootingcalendar",
        icon: CalendarCheck,
      },
    ],
  },
  {
    id: "budget",
    label: "Budget",
    icon: Sheet,
    segment: "budget",
    studioOnly: true,
    primaryFeature: "budget",
    secondaryFeatures: ["tax_incentives"],
    studioLockDescription:
      "Budgets, fringes and tax incentives come with a Studio license. Your script and characters stay as they are.",
    items: [
      {
        id: "budget-production-budget",
        label: "Production Budget",
        segment: "breakdown",
        icon: PieChart,
      },
      { id: "budget-fringes", label: "Fringes", segment: "fringes", icon: Percent },
      { id: "budget-actuals", label: "Actuals", segment: "actuals", icon: Receipt },
      {
        id: "budget-tax-incentives",
        label: "Tax Incentives",
        segment: "taxincentives",
        icon: Landmark,
      },
    ],
  },
];

export const PROJECT_LINKS: readonly ProjectLinkDef[] = [
  {
    id: "project-settings",
    label: "Project settings",
    sidebarLabel: "Settings",
    inSidebar: true,
    icon: Settings,
    segment: "settings/general",
    // The settings index redirects to general, and the danger zone is a part of project settings.
    matchSegments: ["settings/general", "settings/danger"],
  },
  {
    id: "team-members",
    label: "Team members",
    sidebarLabel: "Team",
    inSidebar: true,
    icon: UserRound,
    segment: "settings/members",
    matchSegments: ["settings/members"],
  },
  {
    id: "ai-credits",
    label: "AI credits",
    sidebarLabel: "AI credits",
    // The Run AI button in the header shows the balance; the page stays in
    // the palette and in project settings.
    inSidebar: false,
    icon: Sparkles,
    segment: "settings/credits",
    matchSegments: ["settings/credits"],
  },
];

/** The project links the sidebar lists, in order. */
export const SIDEBAR_PROJECT_LINKS: readonly ProjectLinkDef[] = PROJECT_LINKS.filter(
  (link) => link.inSidebar
);

/** Default landing page of a project, used when switching projects. */
export const PROJECT_DEFAULT_SEGMENT = "script/breakdown";

export const getProjectStep = (stepId: ProjectStepId): ProjectStepDef => {
  const step = PROJECT_STEPS.find((candidate) => candidate.id === stepId);
  if (!step) {
    throw new Error(`Unknown project step: ${stepId}`);
  }
  return step;
};

export const getProjectStepItemSegment = (step: ProjectStepDef, item: ProjectStepItemDef) =>
  `${step.segment}/${item.segment}`;

export const buildProjectBasePath = (workspaceId: string, projectId: string) =>
  `/workspaces/${workspaceId}/projects/${projectId}`;

export const buildProjectHref = (workspaceId: string, projectId: string, segment: string) =>
  `${buildProjectBasePath(workspaceId, projectId)}/${segment}`;

export type ProjectNavLocation =
  | { kind: "step"; stepId: ProjectStepId; itemId: string | null; segment: string }
  | { kind: "link"; linkId: ProjectLinkId; segment: string }
  | { kind: "none"; segment: string | null };

const PROJECT_PATH_PATTERN = /^\/workspaces\/[^/]+\/projects\/[^/]+(?:\/(.*))?$/;

const isSegmentPrefix = (path: string, prefix: string) =>
  path === prefix || path.startsWith(`${prefix}/`);

/**
 * Resolves which step, item or project link a pathname belongs to. Matching
 * works on whole path segments, so `/script` never matches `/script-notes`.
 */
export function resolveProjectNavLocation(pathname: string): ProjectNavLocation {
  const match = PROJECT_PATH_PATTERN.exec(pathname.replace(/\/+$/, ""));
  if (!match) {
    return { kind: "none", segment: null };
  }

  const segment = match[1] ?? "";
  const [first = "", ...rest] = segment.split("/");
  const step = PROJECT_STEPS.find((candidate) => candidate.segment === first);

  if (step) {
    const sub = rest.join("/");
    const item =
      step.items.find((candidate) => isSegmentPrefix(sub, candidate.segment)) ??
      step.items.find((candidate) =>
        (candidate.alsoMatches ?? []).some((alias) => isSegmentPrefix(sub, alias))
      );
    return { kind: "step", stepId: step.id, itemId: item?.id ?? null, segment };
  }

  if (segment === "settings") {
    return { kind: "link", linkId: "project-settings", segment };
  }

  const link = PROJECT_LINKS.find((candidate) =>
    candidate.matchSegments.some((prefix) => isSegmentPrefix(segment, prefix))
  );
  if (link) {
    return { kind: "link", linkId: link.id, segment };
  }

  return { kind: "none", segment };
}

/**
 * Where to land when switching to another project from `pathname`: the same
 * page when every project has it, otherwise the project's default page.
 * Studio steps are excluded because the target project may hold a Writer license.
 */
export function resolveProjectSwitchSegment(pathname: string): string {
  const location = resolveProjectNavLocation(pathname);
  if (location.kind === "step") {
    const step = getProjectStep(location.stepId);
    const item = step.items.find((candidate) => candidate.id === location.itemId);
    if (!step.studioOnly && item) {
      return getProjectStepItemSegment(step, item);
    }
    return PROJECT_DEFAULT_SEGMENT;
  }
  if (location.kind === "link") {
    const link = PROJECT_LINKS.find((candidate) => candidate.id === location.linkId);
    return link?.segment ?? PROJECT_DEFAULT_SEGMENT;
  }
  return PROJECT_DEFAULT_SEGMENT;
}
